// client/src/v2/doc.ts
// O DOCUMENTO — o corpo da nota deixa de ser marcação e vira estrutura.
//
// ── POR QUE NÃO GUARDAR HTML ────────────────────────────────────────────
//
// Guardar HTML obriga a PENEIRAR: receber marcação de origem desconhecida e
// tentar reconhecer o que nela é perigoso. Esse jogo se perde por um motivo
// estrutural — HTML é ambíguo. A mesma sequência de caracteres pode ser lida
// de um jeito pelo filtro e de outro pelo navegador, e é dessa fresta que vive
// uma família inteira de ataques. Um filtro de HTML é uma aposta que precisa
// acertar todas as vezes; quem ataca precisa acertar uma.
//
// O Documentos do Google não joga esse jogo. Lá o documento é uma estrutura:
// texto puro com atributos enumerados (negrito: sim; tamanho: 11). Não existe
// etiqueta, não existe atributo, não existe endereço — e por isso colar de um
// site nunca executou nada.
//
// É esse o modelo aqui.
//
//   [{ t: 'p', c: [ { s: 'Terminar o ' }, { s: 'Sapiens', b: 1 } ] }]
//
//   t — o tipo do bloco, de uma lista fechada
//   c — os pedaços; `s` é TEXTO PURO, o resto são sinalizadores 0/1
//
// O que isso resolve, e é o ponto todo: NÃO HÁ O QUE PENEIRAR. Uma linha do
// banco adulterada à mão, um `curl` malicioso, uma falha futura no servidor —
// o pior que qualquer um deles consegue produzir é texto com negrito. Não
// existe caminho, nem em teoria, de um documento virar script: para virar
// script seria preciso uma etiqueta, e o modelo não tem onde guardar uma.
//
// O contentEditable continua produzindo HTML NA TELA — é assim que o navegador
// edita texto. Mas esse HTML é descartável: a cada tecla o documento é LIDO do
// DOM e vira modelo. O que foi colado e não cabe no modelo simplesmente não
// tem para onde ir. O HTML nunca dá a volta: nunca sai do banco para a tela.

export type Marca = 'b' | 'i' | 'u' | 'x' | 'm';
/** `f` (letra) e `z` (tamanho) moram AQUI, junto do texto, e não na folha.
 *
 *  Era coluna da nota: escolher serifada trocava a folha inteira. Agora vale
 *  para o trecho selecionado — ou, com o cursor parado, do próximo caractere
 *  em diante — exatamente como o negrito. O que já está escrito não muda. */
export type Pedaco = {
  s: string; b?: 1; i?: 1; u?: 1; x?: 1; m?: 1;
  f?: string; z?: string;
};
/** `e` é o espaçamento entrelinhas, e mora no BLOCO — não no pedaço.
 *
 *  Entrelinha é uma propriedade da LINHA: não existe "meia palavra mais
 *  espaçada". Por isso ela acompanha o parágrafo, enquanto letra e tamanho
 *  acompanham o trecho. Ausente = a base da folha. */
export type Bloco = { t: string; c: Pedaco[]; e?: string };
export type Doc = Bloco[];

/** Os tipos de bloco que existem. Fora desta lista nada é aceito — o bloco
 *  desconhecido vira parágrafo, e o texto dele é preservado. */
export const TIPOS = ['p', 'h2', 'h3', 'quote', 'li', 'oli'];
const MARCAS: Marca[] = ['b', 'i', 'u', 'x', 'm'];
/** Listas fechadas, como tudo neste modelo: `f` e `z` são CHAVES, não valores
 *  de CSS. Uma chave vira nome de classe (`f-lora`), e o que a classe faz está
 *  escrito na folha de estilo do editor. Em nenhum momento um texto guardado
 *  vira estilo — o pior que uma chave inventada consegue é ser descartada. */
export const LETRAS = ['sans', 'serif', 'mono', 'literata', 'lora', 'garamond', 'legivel', 'mao'];
export const CORPOS = ['p', 'm', 'g', 'gg'];
/** Os mesmos degraus do Documentos do Google: Simples, 1,15 (o padrão de lá),
 *  1,5 e Duplo. */
export const ESPACOS = ['1', '115', '15', '2'];

/** `<font size="N">` é o que o navegador produz no comando de tamanho. */
const DE_TAMANHO: Record<string, string> = { '1': 'p', '2': 'm', '3': 'g', '4': 'gg' };
export const PARA_TAMANHO: Record<string, string> = { p: '1', m: '2', g: '3', gg: '4' };

// Limites generosos, mas limites.
//
// ERAM 60 MIL LETRAS E 4 MIL BLOCOS — tamanho de uma nota. Desde que o caderno
// pessoal virou UM documento (30/09/2026), o limite passou a valer para o
// caderno inteiro, e ninguém percebeu: medido em 01/10, escrever depois de ~60
// mil letras (umas 10 mil palavras) aparecia na tela e NÃO era guardado, e ao
// reabrir tinha sumido. Agora são 200 mil letras (~35 mil palavras, umas 80
// páginas de livro) — e o caderno AVISA antes de chegar lá.
//
// Exportados porque quem avisa precisa do mesmo número de quem corta. Dois
// números iguais escritos em dois lugares divergem na primeira mudança.
export const MAX_BLOCOS = 20000;
const MAX_PEDACOS = 800;
export const MAX_TEXTO = 200000;

/** Da etiqueta do navegador para o sinalizador do modelo. */
const DE_ETIQUETA: Record<string, Marca> = {
  B: 'b', STRONG: 'b',
  I: 'i', EM: 'i',
  U: 'u', INS: 'u',
  S: 'x', STRIKE: 'x', DEL: 'x',
  MARK: 'm',
};

/** Do sinalizador para a etiqueta — o caminho de volta, sempre por
 *  `createElement` a partir desta tabela. Nunca a partir do que veio guardado. */
const PARA_ETIQUETA: Record<Marca, string> = { b: 'b', i: 'i', u: 'u', x: 's', m: 'mark' };

const DE_BLOCO: Record<string, string> = {
  H1: 'h2', H2: 'h2', H3: 'h3', H4: 'h3', H5: 'h3', H6: 'h3',
  BLOCKQUOTE: 'quote',
  DIV: 'p', P: 'p',
};

/** O documento vindo de fora — do banco, da rede, de onde for — reduzido ao que
 *  o modelo permite.
 *
 *  Não descarta a nota por causa de um campo errado: o bloco desconhecido vira
 *  parágrafo e o texto continua lá. Perder o que a pessoa escreveu por causa de
 *  uma etiqueta seria o pior tipo de zelo. */
export function validar(bruto: any): Doc {
  let d = bruto;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return []; }
  }
  if (!Array.isArray(d)) return [];

  const saida: Doc = [];
  let gasto = 0;
  for (const b of d.slice(0, MAX_BLOCOS)) {
    if (!b || typeof b !== 'object') continue;
    const t = TIPOS.includes((b as any).t) ? (b as any).t : 'p';
    const pedacos: Pedaco[] = [];
    const lista = Array.isArray((b as any).c) ? (b as any).c : [];
    for (const r of lista.slice(0, MAX_PEDACOS)) {
      if (!r || typeof r !== 'object' || typeof r.s !== 'string') continue;
      if (gasto >= MAX_TEXTO) break;
      // O ESPAÇO DE LARGURA ZERO NÃO É CONTEÚDO.
      //
      // O editor usa um `\u200B` como âncora: escolher uma letra com o cursor
      // parado cria um `<span class="f-lora">` com ele dentro, e o que for
      // digitado a seguir nasce ali. É o "daqui pra frente" — o navegador
      // guarda estilo pendente para negrito e itálico, mas NÃO para família e
      // corpo, então essa âncora é quem faz a promessa valer.
      //
      // Ele morre aqui, antes de virar documento: é andaime, não texto. Some
      // da prévia, da busca e da contagem de palavras.
      const s = String(r.s).replace(/\u200B/g, '').slice(0, MAX_TEXTO - gasto);
      gasto += s.length;
      if (!s) continue;
      const p: Pedaco = { s };
      // Só o que está na lista, e só como 1. Um `b: "<script>"` vira `b: 1`.
      for (const m of MARCAS) if ((r as any)[m]) (p as any)[m] = 1;
      // `f` e `z` só atravessam se forem CHAVES conhecidas. Qualquer outra
      // coisa — inclusive um pedaço de CSS — é descartada.
      if (LETRAS.includes((r as any).f)) p.f = (r as any).f;
      if (CORPOS.includes((r as any).z)) p.z = (r as any).z;
      pedacos.push(p);
    }
    const bloco: Bloco = { t, c: pedacos };
    if (ESPACOS.includes((b as any).e)) bloco.e = (b as any).e;
    saida.push(bloco);
  }
  return saida;
}

/** O documento virado em nós de verdade, para entrar na tela.
 *
 *  Todo elemento sai de `createElement` com nome vindo das tabelas acima.
 *  Nenhum atributo é posto. É por construção que não há como injetar nada:
 *  não existe ponto neste caminho onde uma string vire marcação. */
export function paraDom(doc: Doc): DocumentFragment {
  const pedaco = document.createDocumentFragment();
  const blocos = validar(doc);
  let i = 0;

  while (i < blocos.length) {
    const b = blocos[i];

    // Itens seguidos viram uma lista só — senão cada item viraria uma lista
    // de um item, e o marcador ficaria espaçado.
    if (b.t === 'li' || b.t === 'oli') {
      const lista = document.createElement(b.t === 'li' ? 'ul' : 'ol');
      const tipo = b.t;
      while (i < blocos.length && blocos[i].t === tipo) {
        const item = document.createElement('li');
        // A entrelinha vai no ITEM, não na lista: dois itens da mesma lista
        // podem ter espaçamentos diferentes.
        if (blocos[i].e) item.className = 'e-' + blocos[i].e;
        encher(item, blocos[i].c);
        lista.appendChild(item);
        i++;
      }
      pedaco.appendChild(lista);
      continue;
    }

    const el = document.createElement(
      b.t === 'h2' ? 'h2' : b.t === 'h3' ? 'h3' : b.t === 'quote' ? 'blockquote' : 'div'
    );
    if (b.e) el.className = 'e-' + b.e;
    encher(el, b.c);
    pedaco.appendChild(el);
    i++;
  }

  if (!pedaco.childNodes.length) pedaco.appendChild(document.createElement('div'));
  return pedaco;
}

function encher(el: HTMLElement, pedacos: Pedaco[]) {
  if (!pedacos.length || !pedacos.some((p) => p.s)) {
    // Bloco vazio precisa de um <br> para ter altura e receber o cursor.
    el.appendChild(document.createElement('br'));
    return;
  }
  for (const p of pedacos) {
    let no: Node = document.createTextNode(p.s);
    for (const m of MARCAS) {
      if (!(p as any)[m]) continue;
      const volta = document.createElement(PARA_ETIQUETA[m]);
      volta.appendChild(no);
      no = volta;
    }
    // O TAMANHO VAI POR DENTRO DA LETRA, e isso decide se a coisa funciona.
    //
    // Duas famílias no mesmo corpo em pixels não parecem do mesmo tamanho: o
    // que o olho lê é a altura-de-x. Então cada família declara uma escala, e
    // o tamanho é `calc(20px * var(--escala))`. Para a conta enxergar a escala
    // da família, o elemento do tamanho precisa estar DENTRO do elemento da
    // família — variável de CSS desce, não sobe.
    //
    // Invertido, um trecho em Caveat no corpo grande sairia no tamanho da
    // Geist: pequeno demais, e ninguém saberia dizer por quê.
    if (p.z) no = embrulhar(no, 'z-' + p.z);
    if (p.f) no = embrulhar(no, 'f-' + p.f);
    el.appendChild(no);
  }
}

/** O único atributo que este arquivo escreve, e o valor vem de `'f-' + chave`
 *  com a chave já validada contra a lista fechada. Nunca de texto guardado. */
function embrulhar(no: Node, classe: string): Node {
  const span = document.createElement('span');
  span.className = classe;
  span.appendChild(no);
  return span;
}

/** O que o navegador desenhou, lido de volta como documento.
 *
 *  É aqui que o HTML morre: o que o editor produziu — ou o que foi colado — é
 *  PERCORRIDO, e só o que corresponde ao modelo atravessa. Uma `<img>` colada
 *  não vira nada porque não existe pedaço de imagem; um `onerror` não vira nada
 *  porque atributo não é lido. Não é uma peneira que tenta reconhecer perigo;
 *  é uma leitura que só reconhece o que permite. */
export function deDom(raiz: Node): Doc {
  const doc: Doc = [];

  const lerPedacos = (no: Node, marcas: Set<Marca>, destino: Pedaco[]) => {
    for (const filho of Array.from(no.childNodes)) {
      if (filho.nodeType === Node.TEXT_NODE) {
        const s = filho.nodeValue || '';
        if (!s) continue;
        const p: Pedaco = { s };
        marcas.forEach((m) => { (p as any)[m] = 1; });
        destino.push(p);
        continue;
      }
      if (filho.nodeType !== Node.ELEMENT_NODE) continue;
      const el = filho as HTMLElement;
      const nome = el.tagName.toUpperCase();
      if (nome === 'BR') { destino.push({ s: '\n' }); continue; }

      // LETRA E TAMANHO vêm de dois lugares, e os dois são lidos como CHAVE:
      //   · `<span class="f-lora">`  — o que este arquivo mesmo desenhou;
      //   · `<font face="lora">`     — o que o navegador produz no comando.
      // O comando do navegador é usado de propósito: é ele que sabe partir a
      // seleção no meio de uma palavra e guardar o estilo pendente quando o
      // cursor está parado. O `<font>` nunca chega ao banco — morre aqui.
      const letra = chaveDe(el, 'f', LETRAS);
      const corpo = chaveDe(el, 'z', CORPOS);
      const antes = destino.length;
      const marca = DE_ETIQUETA[nome];
      // Elemento fora da tabela (span, img, a, script…): desce para os filhos.
      // O que ele "era" não acompanha — não há onde guardar.
      if (marca) {
        const dobrado = new Set(marcas); dobrado.add(marca);
        lerPedacos(el, dobrado, destino);
      } else {
        lerPedacos(el, marcas, destino);
      }
      // O de fora não sobrescreve o de dentro: um trecho em Lora dentro de um
      // bloco posto em Geist continua em Lora.
      if (letra || corpo) {
        for (let k = antes; k < destino.length; k++) {
          if (letra && !destino[k].f) destino[k].f = letra;
          if (corpo && !destino[k].z) destino[k].z = corpo;
        }
      }
    }
  };

  const abrirBloco = (tipo: string, no: Node) => {
    const espaco = no.nodeType === 1 ? chaveDe(no as HTMLElement, 'e', ESPACOS) : null;
    const pedacos: Pedaco[] = [];
    lerPedacos(no, new Set(), pedacos);

    // O `<br>` DO FIM É ENCHIMENTO, NÃO QUEBRA.
    //
    // O navegador escreve linha vazia como `<div><br></div>` — o `<br>` existe
    // só para o bloco ter altura e receber o cursor. Tratá-lo como quebra fazia
    // um bloco vazio virar DOIS a cada leitura, e como o documento é lido a
    // cada tecla e escrito de volta, o número de linhas vazias DOBRAVA a cada
    // ida e volta: 31 viravam 62, estouravam a folha e se espalhavam em seis.
    while (pedacos.length && pedacos[pedacos.length - 1].s === '\n') pedacos.pop();

    // `<br>` no meio de um bloco vira bloco novo: no editor a pessoa vê uma
    // linha, e uma linha é um bloco.
    const fechar = (c: Pedaco[]) => {
      const b: Bloco = { t: tipo, c: juntarIguais(c) };
      if (espaco) b.e = espaco;
      doc.push(b);
    };
    let atual: Pedaco[] = [];
    for (const p of pedacos) {
      if (p.s === '\n') { fechar(atual); atual = []; continue; }
      atual.push(p);
    }
    fechar(atual);
  };

  const percorrer = (no: Node, solto: Pedaco[]) => {
    for (const filho of Array.from(no.childNodes)) {
      if (filho.nodeType === Node.TEXT_NODE) {
        const s = filho.nodeValue || '';
        if (s.trim()) solto.push({ s });
        continue;
      }
      if (filho.nodeType !== Node.ELEMENT_NODE) continue;
      const el = filho as HTMLElement;
      const nome = el.tagName.toUpperCase();

      if (nome === 'UL' || nome === 'OL') {
        despejar(solto);
        const tipo = nome === 'UL' ? 'li' : 'oli';
        Array.from(el.children).forEach((li) => {
          if (li.tagName.toUpperCase() === 'LI') abrirBloco(tipo, li);
        });
        continue;
      }
      if (DE_BLOCO[nome]) { despejar(solto); abrirBloco(DE_BLOCO[nome], el); continue; }
      if (nome === 'BR') { despejar(solto); doc.push({ t: 'p', c: [] }); continue; }

      // Inline solto na raiz (o editor faz isso na primeira linha): junta no
      // parágrafo que está sendo formado.
      const pedacos: Pedaco[] = [];
      lerPedacos(el, new Set(), pedacos);
      const marca = DE_ETIQUETA[nome];
      if (marca) pedacos.forEach((p) => { (p as any)[marca] = 1; });
      solto.push(...pedacos);
    }
  };

  const despejar = (solto: Pedaco[]) => {
    if (!solto.length) return;
    doc.push({ t: 'p', c: juntarIguais(solto.splice(0, solto.length)) });
  };

  const sobra: Pedaco[] = [];
  percorrer(raiz, sobra);
  despejar(sobra);

  return validar(doc);
}

/** A chave de letra ou de tamanho que um elemento carrega, venha ela da classe
 *  que desenhamos ou do `<font>` que o navegador produziu. */
function chaveDe(el: HTMLElement, qual: 'f' | 'z' | 'e', lista: string[]): string | null {
  const nome = el.tagName.toUpperCase();
  if (nome === 'FONT') {
    if (qual === 'f') {
      const face = (el.getAttribute('face') || '').trim();
      return lista.includes(face) ? face : null;
    }
    const tamanho = DE_TAMANHO[(el.getAttribute('size') || '').trim()];
    return tamanho && lista.includes(tamanho) ? tamanho : null;
  }
  for (const c of Array.from(el.classList || [])) {
    if (!c.startsWith(qual + '-')) continue;
    const chave = c.slice(2);
    if (lista.includes(chave)) return chave;
  }
  return null;
}

/** Pedaços vizinhos com as mesmas marcas viram um só — senão cada tecla
 *  digitada acabaria virando um pedaço, e o documento incharia. */
function juntarIguais(pedacos: Pedaco[]): Pedaco[] {
  const saida: Pedaco[] = [];
  for (const p of pedacos) {
    if (!p.s) continue;
    const ultimo = saida[saida.length - 1];
    const mesmas = ultimo
      && MARCAS.every((m) => !!(ultimo as any)[m] === !!(p as any)[m])
      && ultimo.f === p.f && ultimo.z === p.z;
    if (mesmas) ultimo.s += p.s;
    else saida.push({ ...p });
  }
  return saida;
}

/** Marcação de fora — do que foi colado, ou de uma nota guardada antes desta
 *  mudança — lida como documento. Passa pelo MESMO caminho: monta num
 *  documento inerte, que não executa script nem carrega imagem, e lê. */
export function deHtml(html: string): Doc {
  const inerte = new DOMParser().parseFromString(
    `<!DOCTYPE html><body>${String(html || '')}</body>`, 'text/html'
  );
  inerte.body.querySelectorAll('script, style, noscript, template').forEach((n) => n.remove());
  return deDom(inerte.body);
}

/** Texto puro do bloco — para prévia, busca e para achar `#tag` no corpo. */
export function paraTexto(doc: Doc): string {
  return validar(doc)
    .map((b) => b.c.map((p) => p.s).join(''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Markdown antigo virando documento. A folha escrita antes desta mudança é
 *  convertida na hora de abrir; o banco só muda quando a pessoa editar. */
export function deMarkdown(md: string): Doc {
  const doc: Doc = [];
  const inline = (t: string): Pedaco[] => {
    const saida: Pedaco[] = [];
    const re = /(\*\*[^*]+\*\*|_[^_]+_)/g;
    let ultimo = 0; let m: RegExpExecArray | null;
    while ((m = re.exec(t)) !== null) {
      if (m.index > ultimo) saida.push({ s: t.slice(ultimo, m.index) });
      const bruto = m[0];
      if (bruto.startsWith('**')) saida.push({ s: bruto.slice(2, -2), b: 1 });
      else saida.push({ s: bruto.slice(1, -1), i: 1 });
      ultimo = m.index + bruto.length;
    }
    if (ultimo < t.length) saida.push({ s: t.slice(ultimo) });
    return saida.filter((p) => p.s);
  };

  for (const bruta of String(md || '').split('\n')) {
    const l = bruta.replace(/\s+$/, '');
    if (!l.trim()) { doc.push({ t: 'p', c: [] }); continue; }

    const h = l.match(/^(#{1,6})\s+(.*)$/);
    if (h) { doc.push({ t: h[1].length <= 2 ? 'h2' : 'h3', c: inline(h[2]) }); continue; }

    const cit = l.match(/^>\s?(.*)$/);
    if (cit) { doc.push({ t: 'quote', c: inline(cit[1]) }); continue; }

    const chk = l.match(/^\s*[-*+]\s+\[([ xX])\]\s*(.*)$/);
    if (chk) {
      doc.push({ t: 'li', c: [{ s: (chk[1].toLowerCase() === 'x' ? '☑' : '☐') + ' ' }, ...inline(chk[2])] });
      continue;
    }

    const item = l.match(/^\s*[-*+]\s+(.*)$/);
    if (item) { doc.push({ t: 'li', c: inline(item[1]) }); continue; }

    doc.push({ t: 'p', c: inline(l.replace(/\[\[([^\]]+)\]\]/g, '$1')) });
  }
  return doc;
}

/** O corpo de uma nota como documento, venha ele de onde vier. */
export function paraDocumento(nota: { text?: string; formato?: string } | null): Doc {
  if (!nota) return [];
  const t = String(nota.text || '');
  if (nota.formato === 'doc') return validar(t);
  // 'html' foi o passo intermediário de 24/09/2026 e existe em poucas notas;
  // a folha antiga é markdown. As duas entram pelo mesmo modelo.
  if (nota.formato === 'html') return deHtml(t);
  return deMarkdown(t);
}

/** O corpo de uma nota como texto puro, venha ele de onde vier.
 *
 *  A prévia, a busca e o compartilhar leem por aqui. Cada tela resolvendo por
 *  conta própria é como se chega a duas verdades. */
export function textoPuro(nota: { text?: string; formato?: string } | null): string {
  if (!nota) return '';
  const t = String(nota.text || '');
  if (nota.formato === 'doc') return paraTexto(validar(t));
  if (nota.formato === 'html') return paraTexto(deHtml(t));
  return t
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*[-*+]\s+\[[ xX]\]\s*/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .trim();
}
